import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Field,
  Icon,
  Modal,
  ModalActions,
  MultiSelect,
  Page,
  PageHeader,
  Pagination,
  Select,
  Table,
  type Action,
} from '@evogard/ui';
import type { Department, TenantUser } from '@evogard/contract';
import { canAccess, formatInstant } from '@evogard/domain';
import { api, errorMessage, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { UserPermissionExceptionsModal } from './UserPermissionExceptionsModal.js';
import { UserIdentityForm } from '../../components/UserIdentityForm.js';

export function UsersScreen() {
  const client = useQueryClient();
  const { data: context } = useSessionContext();
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** O aviso do convite reenviado — a data vem da resposta, não de um número escrito aqui. */
  const [sent, setSent] = useState<string | null>(null);
  const term = useDebouncedTerm(search);

  const canEdit = can(context, 'platform.users', 'editar');
  const canBlock = can(context, 'platform.users', 'bloquear');
  const canManageExceptions = can(context, 'platform.users', 'gerenciar_excecoes');

  const canSeeGroups = can(context, 'platform.groups', 'ver');

  /*
   * Ver e editar departamento são do recurso `platform.departments`, não de
   * `platform.users` — a razão está no catálogo de permissões: mexer na fila
   * muda o roteamento das demandas, e quem cadastra pessoa não é
   * necessariamente quem decide qual setor atende o quê.
   *
   * A coluna some inteira para quem não pode ver, em vez de aparecer vazia:
   * uma coluna "Departamentos" em branco afirma que a pessoa não está em
   * nenhum, e isso seria falso.
   */
  const canSeeDepartments = can(context, 'platform.departments', 'ver');
  const canEditDepartments = can(context, 'platform.departments', 'editar');

  const departments = useQuery({
    queryKey: ['plataforma', 'departamentos', context?.currentTenant.id, false],
    enabled: canSeeDepartments,
    queryFn: async () => {
      const r = await api.platform.listDepartments({ query: {} });
      return ok(r, 200, 'Não foi possível carregar os departamentos.');
    },
  });

  /*
   * Mesma consulta e mesma chave que `GroupEditor.tsx` usa — o catálogo é
   * escrito à mão em sete módulos e não muda entre navegações, e as duas
   * telas compartilham o cache em vez de cada uma buscar o seu.
   */
  const catalog = useQuery({
    queryKey: ['plataforma', 'catalogo-permissoes'],
    enabled: canManageExceptions,
    queryFn: async () => {
      const r = await api.platform.catalogPermissions();
      return ok(r, 200, 'Não foi possível carregar o catálogo.');
    },
    staleTime: Infinity,
  });

  /*
   * A lista de grupos é da rota `/plataforma/grupos`, que exige
   * `platform.groups:ver` — e **não** `platform.users:editar`.
   *
   * Enquanto o `enabled` olhava só para `editar`, quem tinha "Usuários: ver,
   * editar, bloquear" e nada em Grupos disparava a consulta, levava 403, e o
   * erro não era mostrado em lugar nenhum: `isLoading` virava `false`, o
   * `select` renderizava HABILITADO com "Sem grupo" como única opção, e
   * `value={u.grupoId}` não casava com nenhuma delas. A tela mostrava a base
   * inteira — administradores inclusive — como "Sem grupo", e um clique
   * naquele seletor tirava de verdade o grupo de alguém.
   */
  const groups = useQuery({
    queryKey: ['plataforma', 'grupos', context?.currentTenant.id],
    enabled: canEdit && canSeeGroups,
    queryFn: async () => {
      const r = await api.platform.listGroups();
      return ok(r, 200, 'Não foi possível carregar os grupos.');
    },
  });

  /*
   * Trocar o grupo só é oferecido quando a lista de grupos existe de verdade.
   * Sem ela o seletor não tem como mostrar o grupo atual — e um seletor que
   * mostra a opção errada é pior do que um texto que mostra a certa.
   */
  const canSwitchGroup = canEdit && canSeeGroups && !groups.isError;

  /**
   * Quais grupos concedem `tickets.tickets:liderar` — para o aviso da marca inerte.
   *
   * Sai da lista que a tela JÁ carrega, e não de uma consulta nova: as
   * permissões vêm em `groupSchema.permissions`. Se a lista não veio (sem
   * `platform.groups:ver`, ou erro), o conjunto fica vazio e o aviso não
   * aparece — silêncio é melhor do que avisar que a marca não vale quando a tela
   * não tem como saber.
   *
   * `canAccess` e não `includes`: o curinga `*:*` concede `liderar` sem que a
   * string apareça na lista, e um `includes` diria que o grupo de administração
   * não lidera nada.
   */
  const leadingGroups = new Set(
    (groups.data ?? [])
      .filter((g) => canAccess(g.permissions, 'tickets.tickets', 'liderar'))
      .map((g) => g.id),
  );

  const reload = () => {
    void client.invalidateQueries({ queryKey: ['plataforma', 'usuarios'] });
    /*
     * O contexto também: quem administra pode ter mudado o próprio grupo, e é
     * o contexto que decide o que a interface esconde.
     */
    void client.invalidateQueries({ queryKey: ['plataforma', 'contexto'] });
  };

  const switchGroup = useMutation({
    mutationFn: async ({ id, groupId }: { id: string; groupId: string | null }) => {
      const r = await api.platform.linkUser({
        params: { id },
        body: { groupId: groupId },
      });
      if (r.status !== 200) {
        throw new Error(
          (r.body as { message?: string }).message ?? 'Não foi possível trocar o grupo.',
        );
      }
    },
    onSuccess: () => {
      setError(null);
      reload();
    },
    onError: (e: Error) => setError(e.message),
  });

  const block = useMutation({
    mutationFn: async ({ id, blocked }: { id: string; blocked: boolean }) => {
      const r = await api.platform.blockUser({
        params: { id },
        body: { blocked },
      });
      if (r.status !== 200) {
        throw new Error(
          (r.body as { message?: string }).message ?? 'Não foi possível bloquear.',
        );
      }
    },
    onSuccess: () => {
      setError(null);
      reload();
    },
    onError: (e: Error) => setError(e.message),
  });

  /**
   * Reenviar o convite de primeiro acesso, da linha da pessoa.
   *
   * Existe porque as duas frases do cartão de cadastro mandam fazer isto aqui:
   * quando o convite falha depois de o cadastro ter sido criado, e quando o
   * link vence antes de a pessoa usá-lo. Sem este botão, as duas frases
   * apontavam para uma ação que não existia em lugar nenhum, e o colaborador
   * cadastrado ficava sem caminho de entrada — cadastrar de novo devolve
   * "já existe alguém com esse e-mail nesta base".
   *
   * O convite novo **arquiva o anterior**; é a rota que faz isso, e é o mesmo
   * desenho de `members.invite`.
   */
  const resendInvite = useMutation({
    mutationFn: async (u: TenantUser) => {
      const r = await api.platform.inviteUser({ params: { id: u.id }, body: {} });
      if (r.status !== 201) {
        throw new Error(errorMessage(r.body, 'Não foi possível enviar o convite.'));
      }
      return { email: u.email, expiresAt: r.body.expiresAt };
    },
    onSuccess: ({ email, expiresAt }) => {
      setError(null);
      setSent(`${email} recebeu o link. Ele vale até ${formatInstant(expiresAt)}.`);
    },
    onError: (e: Error) => {
      setSent(null);
      setError(e.message);
    },
  });

  const meSame = (u: TenantUser) => u.id === context?.user.id;

  /*
   * Até 15/09 esta lista pedia `page: 1, perPage: 50` e escrevia embaixo do
   * título que havia mais — o aviso de corte de `lib/list.ts`. Ele existia
   * porque não havia como virar página; agora há, e o aviso saiu junto: dizer
   * "use a busca para chegar ao resto" ao lado de um botão "Próxima" é a tela
   * ensinando o caminho mais longo.
   */
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  /*
   * Estreitar o recorte encurta a lista, e a página em que se estava deixa de
   * existir: buscar um nome estando na página 4 pediria um `offset` além do
   * fim, e a resposta seria uma tabela vazia sob um cartão dizendo o total.
   * `perPage` está aqui pelo mesmo motivo, e a praça também — trocar de base
   * troca a lista inteira.
   */
  useEffect(() => {
    setPage(1);
  }, [context?.currentTenant.id, term, perPage]);

  const usersQuery = useQuery({
    queryKey: ['plataforma', 'usuarios', context?.currentTenant.id, term, page, perPage],
    queryFn: async () => {
      const r = await api.platform.listUsers({
        query: { page, perPage, search: term || undefined },
      });
      return ok(r, 200, 'Não foi possível carregar os usuários.');
    },
    // Mantém a lista anterior enquanto a busca nova chega: sem isto, cada
    // termo digitado apagava a tabela e devolvia um esqueleto piscando. Mas
    // só dentro da mesma base: ao trocar de tenant, segurar o resultado
    // anterior deixaria a tabela e a contagem da base A sob um cabeçalho que
    // já diz o nome da base B. Aí a tela volta ao esqueleto, que é a verdade.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === context?.currentTenant.id ? previous : undefined,
  });

  const total = usersQuery.data?.total;

  /*
   * Guardado pelo ID, não pela linha capturada na abertura: a lista é
   * invalidada a cada gravação de exceção, e o modal precisa enxergar o
   * resultado novo — uma cópia da linha ficaria com a exceção recém-adicionada
   * só depois de fechar e reabrir.
   */
  const [editingExceptionsId, setEditingExceptionsId] = useState<string | null>(null);
  /*
   * Os departamentos abrem num modal PRÓPRIO desde 23/09/2026, e não mais no
   * painel de ações trocando de conteúdo. O usuário é derivado pelo id a cada
   * render, como o das exceções: a lista é invalidada ao salvar, e uma cópia
   * capturada na abertura mostraria o estado de antes.
   */
  const [editingDepartmentsId, setEditingDepartmentsId] = useState<string | null>(null);
  const editingDepartmentsUser =
    usersQuery.data?.items.find((u) => u.id === editingDepartmentsId) ?? null;
  const editingExceptionsUser =
    usersQuery.data?.items.find((u) => u.id === editingExceptionsId) ?? null;
  /* O nome e o e-mail (30/10/2026) — o mesmo formulário da ficha do consultor. */
  const [editingIdentityId, setEditingIdentityId] = useState<string | null>(null);
  const editingIdentityUser =
    usersQuery.data?.items.find((u) => u.id === editingIdentityId) ?? null;

  return (
    <Page measure="cheia">
      <PageHeader title="Usuários">
        Quem tem vínculo com {context?.currentTenant.nome}.
      </PageHeader>

      {canEdit && (
        <NewUser
          groups={canSeeGroups ? (groups.data ?? []) : []}
          created={() =>
            client.invalidateQueries({ queryKey: ['plataforma', 'usuarios'] })
          }
        />
      )}

      {usersQuery.isError && (
        <Alert tone="erro" title="Não foi possível carregar os usuários.">
          Tente de novo em instantes. Se continuar, avise quem administra a plataforma.
        </Alert>
      )}

      {/* A recusa vem do servidor com a frase dele: "você não pode bloquear a
          si mesmo" resolve; "algo deu errado" manda a pessoa tentar de novo. */}
      {error && <Alert tone="erro">{error}</Alert>}

      {sent && (
        <Alert tone="sucesso" title="Convite enviado.">
          {sent} O anterior, se havia, deixou de valer.
        </Alert>
      )}

      {/*
       * A coluna Grupo vira texto, e a tela diz por quê. Antes ela virava um
       * seletor mentindo "Sem grupo" para todo mundo, sem nada explicando.
       */}
      {canEdit && !canSeeGroups && (
        <Alert tone="alerta" title="O grupo aparece só para leitura.">
          Trocar o grupo de alguém exige também a permissão “Grupos e permissões: ver” — é
          dela que sai a lista de grupos desta base.
        </Alert>
      )}
      {canEdit && canSeeGroups && groups.isError && (
        <Alert tone="erro" title="Não foi possível carregar os grupos.">
          A coluna Grupo fica só para leitura até a lista carregar. Tente de novo em
          instantes.
        </Alert>
      )}

      {/*
       * Erro sem dado nenhum: o aviso acima é a resposta inteira.
       *
       * Com o cartão aqui, a tabela anunciava "Ninguém tem vínculo com esta base
       * ainda" logo abaixo do erro — e o título dizia "Carregando…" para sempre,
       * porque `total` fica `undefined` quando a consulta falha. Duas afirmações
       * falsas empilhadas, e as duas com cara de fato. A tela de associados, que
       * é a irmã desta, já fazia esta guarda.
       */}
      {(!usersQuery.isError || usersQuery.data) && (
        <Card title={total === undefined ? 'Carregando…' : labelTotal(total)} bleed>
          <Table
            paginate={false}
            /*
             * A busca na barra da tabela, e não num cartão de recorte acima
             * dela: é o desenho de 17/09, e aqui ela é o recorte inteiro — não
             * há painel de filtros porque não há segundo recorte a declarar.
             * Um gatilho "Filtros" que abrisse um painel vazio seria pior do que
             * a ausência dele.
             */
            search={{
              label: 'Pesquisar usuários',
              placeholder: 'Nome ou E-mail',
              value: search,
              onChange: setSearch,
            }}
            label="Usuários com vínculo nesta base"
            items={usersQuery.data?.items ?? []}
            // `isLoading` e não `isFetching`: só a primeira carga não tem o que
            // mostrar. Refazer a busca mantém a lista de pé.
            loading={usersQuery.isLoading}
            rowKey={(u) => u.id}
            empty={
              term
                ? `Nenhum usuário com “${term}”. A busca olha nome e e-mail.`
                : 'Ninguém tem vínculo com esta base ainda. Quem for vinculado aparece aqui.'
            }
            rowTitle={(u) => u.nome}
            rowSubtitle={(u) => u.email}
            rowIcon="pessoa"
            /*
             * As três ações da linha, num painel só — e com o MOTIVO escrito
             * quando não cabem.
             *
             * Elas eram dois botões numa coluna sem título e um terceiro dentro
             * da coluna "Departamentos", e os três simplesmente não eram
             * renderizados quando não cabiam: nada dizia por que a linha de uma
             * pessoa tinha três botões e a da outra, nenhum. A resposta mais
             * comum era "é você mesmo", e ela nunca aparecia na tela.
             */
            rowActions={(u): Action[] => {
              const self = meSame(u);
              return [
                ...(canEdit
                  ? [
                      {
                        label: 'Editar nome e e-mail',
                        icon: 'pessoa',
                        run: () => setEditingIdentityId(u.id),
                      } satisfies Action,
                    ]
                  : []),
                ...(canEditDepartments && canSeeDepartments
                  ? [
                      {
                        label: 'Trocar os departamentos',
                        icon: 'pessoas',
                        disabled: departments.isError
                          ? 'A lista de departamentos não carregou.'
                          : (false as const),
                        /*
                         * `run`, e não mais `form`: o painel de ações fecha
                         * ANTES de a ação rodar, então o modal próprio abre
                         * sozinho — não há dois véus empilhados.
                         */
                        run: () => setEditingDepartmentsId(u.id),
                      } satisfies Action,
                    ]
                  : []),
                ...(canManageExceptions
                  ? [
                      {
                        label: 'Gerenciar exceções',
                        icon: 'chave',
                        disabled: catalog.isError
                          ? 'O catálogo de permissões não carregou.'
                          : (false as const),
                        run: () => setEditingExceptionsId(u.id),
                      } satisfies Action,
                    ]
                  : []),
                ...(canEdit
                  ? [
                      {
                        /*
                         * Reenviar o convite recusa para quem está bloqueado —
                         * o link levaria a uma senha que não abre nada — e para
                         * si mesmo, que já está dentro. A rota recusa de
                         * qualquer jeito; o que muda é que agora a tela diz por
                         * quê antes do clique.
                         */
                        label: 'Reenviar o convite',
                        icon: 'sino',
                        disabled: self
                          ? 'Você já está dentro.'
                          : u.blockedAt || !u.active
                            ? 'Esta pessoa está bloqueada — o link não abriria nada.'
                            : (false as const),
                        run: () => resendInvite.mutate(u),
                      } satisfies Action,
                    ]
                  : []),
                ...(canBlock
                  ? [
                      {
                        /*
                         * **Dispara no clique, sem confirmação** — é a pendência
                         * registrada no `CLAUDE.md`, e ela não mudou aqui.
                         *
                         * O que mudou é o aviso: o vermelho era a única coisa
                         * entre o ponteiro e uma pessoa perdendo o acesso, e ele
                         * aparecia uma vez por linha, que é o ritmo em que
                         * destaque deixa de destacar. No painel ele aparece UMA
                         * vez, sobre um registro nomeado no cabeçalho — que é
                         * onde o `perigo` do `DESIGN.md` diz que ele deve estar.
                         * A confirmação continua faltando.
                         */
                        label: u.blockedAt ? 'Desbloquear o acesso' : 'Bloquear o acesso',
                        icon: 'bloqueado',
                        tone: u.blockedAt ? ('neutro' as const) : ('perigo' as const),
                        disabled: self
                          ? 'Você não pode bloquear a si mesmo.'
                          : (false as const),
                        run: () => block.mutate({ id: u.id, blocked: !u.blockedAt }),
                      } satisfies Action,
                    ]
                  : []),
              ];
            }}
            columns={[
              { title: 'Nome', cell: (u) => u.nome, minWidth: '10rem' },
              { title: 'E-mail', cell: (u) => u.email },
              {
                title: 'Grupo',
                alignment: 'inicio' as const,
                minWidth: '12rem',
                cell: (u) =>
                  canSwitchGroup ? (
                    <Select
                      /* o rótulo existe para o leitor de tela e some para o olho:
                       a coluna já diz "Grupo", e repeti-lo em cada linha seria
                       ruído — mas sem ele o `select` não tem nome nenhum */
                      label={`Grupo de ${u.nome}`}
                      hiddenLabel
                      value={u.groupId ?? ''}
                      disabled={switchGroup.isPending || groups.isLoading}
                      onChange={(e) =>
                        switchGroup.mutate({
                          id: u.id,
                          groupId: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    >
                      {/* "Sem grupo" é estado real: entra na base e não pode nada.
                        É a negação por omissão, escolhível. */}
                      <option value="">Sem grupo</option>
                      {groups.data?.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.nome}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <span className="text-sm text-texto-suave">
                      {u.groupName ?? 'Sem grupo'}
                    </span>
                  ),
              },
              ...(canSeeDepartments
                ? [
                    {
                      /*
                       * A coluna VOLTOU A SER SÓ LEITURA em 17/09, e é a que
                       * mais crescia da tabela.
                       *
                       * Ela carregava um selo por departamento **mais** o botão
                       * de editar **mais**, quando aberto, o formulário inteiro
                       * de caixas de marcar dentro da célula: a linha de quem
                       * está em seis filas era seis vezes mais alta que a do
                       * vizinho, e abrir a edição de uma empurrava a tabela
                       * inteira para baixo. E a configuração morava numa coluna
                       * de dado, que é o defeito que a coluna de ações existe
                       * para não ter.
                       *
                       * Agora ela mostra o estado e nada mais, com teto de dois
                       * selos e o resto contado — a altura da linha passou a ser
                       * a mesma em toda a tabela, por construção e não por sorte.
                       * Editar é ação, e ação mora no painel das reticências.
                       */
                      title: 'Departamentos',
                      alignment: 'inicio' as const,
                      minWidth: '12rem',
                      cell: (u: TenantUser) => <UserDepartments user={u} />,
                    },
                  ]
                : []),
              ...(canManageExceptions
                ? [
                    {
                      /*
                       * O selo diz o estado e, desde 01/10/2026, abre o modal
                       * das exceções — o mesmo da ação "Gerenciar exceções" da
                       * linha. Quem vê "2 exceções" quer saber quais, e ir ao
                       * menu da linha para descobrir era um passo a mais.
                       * "Nenhuma" continua texto: sem exceção não há o que
                       * listar, e adicionar a primeira é a ação da linha.
                       */
                      title: 'Exceções',
                      minWidth: '9rem',
                      cell: (u: TenantUser) => {
                        const n = u.permissionExceptions.length;
                        return n === 0 ? (
                          <span className="text-sm text-texto-suave">Nenhuma</span>
                        ) : (
                          <Button
                            variant="discreto"
                            size="sm"
                            aria-label={`Ver as exceções de ${u.nome}`}
                            onClick={() => setEditingExceptionsId(u.id)}
                          >
                            <Badge tone="erro">{`${n} exceç${n === 1 ? 'ão' : 'ões'}`}</Badge>
                          </Button>
                        );
                      },
                    },
                  ]
                : []),
              {
                title: 'Situação',
                alignment: 'fim',
                cell: (u) =>
                  u.blockedAt || !u.active ? (
                    <Badge tone="erro">Bloqueado</Badge>
                  ) : (
                    <Badge tone="sucesso">Ativo</Badge>
                  ),
              },
            ]}
          />

          <Pagination
            page={page}
            perPage={perPage}
            total={total ?? 0}
            onChange={setPage}
            onPerPageChange={setPerPage}
            label="usuários"
            /* o cartão é `bleed`; o espaçamento é de quem entra nele */
            className="border-t border-borda-suave px-5 py-4"
          />
        </Card>
      )}

      {editingDepartmentsUser && (
        <DepartmentsModal
          user={editingDepartmentsUser}
          available={departments.data ?? []}
          mayLead={
            editingDepartmentsUser.groupId !== null &&
            leadingGroups.has(editingDepartmentsUser.groupId)
          }
          onClose={() => setEditingDepartmentsId(null)}
        />
      )}

      {editingIdentityUser && (
        <Modal
          open
          onOpenChange={(next) => !next && setEditingIdentityId(null)}
          size="lg"
          title="Editar nome e e-mail"
          descricao={editingIdentityUser.nome}
        >
          <UserIdentityForm userId={editingIdentityUser.id} />
        </Modal>
      )}

      {editingExceptionsUser && (
        <UserPermissionExceptionsModal
          user={editingExceptionsUser}
          catalog={catalog.data ?? []}
          onClose={() => setEditingExceptionsId(null)}
        />
      )}
    </Page>
  );
}

/**
 * Os departamentos da pessoa, na célula — o estado, e nada mais.
 *
 * ## O teto de dois selos
 *
 * Sem ele, a célula crescia com o cadastro: quem está em seis filas dava uma
 * linha seis vezes mais alta que a do vizinho, e a tabela inteira perdia o
 * ritmo por causa de uma pessoa. Dois é o que cabe em 12rem sem quebrar; o
 * resto vira uma contagem, que responde "quantos" sem mentir sobre "quais" — o
 * `title` carrega a lista completa para quem passar o ponteiro, e o painel de
 * ações a mostra inteira ao editar.
 *
 * "Nenhum" é estado real e tem consequência: a pessoa não recebe aviso de
 * demanda nova de setor nenhum. Dito, não deixado em branco.
 */
function UserDepartments({ user }: { user: TenantUser }) {
  if (user.departments.length === 0) {
    return <span className="text-sm text-texto-suave">Nenhum</span>;
  }

  const shown = user.departments.slice(0, 2);
  const rest = user.departments.length - shown.length;

  return (
    <span
      className="flex flex-wrap items-center gap-1.5"
      title={user.departments.map((d) => d.nome).join(', ')}
    >
      {shown.map((d) => (
        /* o líder se distingue pelo TEXTO, não só pela cor — um selo de tom
           diferente diria "é outro tipo de coisa" a quem não vê a diferença */
        <Badge key={d.id} tone={d.leads ? 'informacao' : 'neutro'}>
          {d.nome}
          {d.leads && ' · lidera'}
        </Badge>
      ))}
      {rest > 0 && <span className="text-xs text-texto-suave">+{rest}</span>}
    </span>
  );
}

/**
 * Trocar os departamentos de alguém — num modal próprio, desde 23/09/2026.
 *
 * ## O desenho, e o que dele não foi copiado
 *
 * A anatomia é a do pedido do responsável: faixa de cabeçalho com voltar,
 * título, o nome da pessoa e o fechar; a linha de perfil com as iniciais; o
 * seletor de departamentos com busca; a liderança; o rodapé com Salvar e
 * Cancelar. As CORES não foram copiadas do pedido: vêm dos tokens do tema, que
 * é o que mantém o modal certo no claro e no escuro — hexadecimal escrito à
 * mão a régua do desenho reprova, e o laranja do pedido é a paleta do portal.
 *
 * ## A liderança é POR departamento
 *
 * O pedido tinha uma caixa só. Mas a pessoa pode estar em três setores e
 * liderar um deles, e uma caixa única não teria como dizer qual. Então cada
 * setor marcado ganha a sua caixa de liderança logo abaixo do seletor.
 *
 * ## Manda o estado final, como a edição de grupo
 *
 * O `PUT` substitui a lista inteira. Calcular a diferença no cliente criaria uma
 * segunda verdade sobre em que filas a pessoa está — e a primeira já é o banco.
 */
function DepartmentsModal({
  user,
  available,
  mayLead,
  onClose,
}: {
  user: TenantUser;
  available: Array<Pick<Department, 'id' | 'nome' | 'leaderGroup'>>;
  /** o grupo DESTA pessoa concede `tickets.tickets:liderar` */
  mayLead: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();
  /*
   * O rascunho é um mapa id → lidera, e não duas listas.
   *
   * Com `chosen: string[]` e `leads: string[]` em paralelo, desmarcar o setor
   * deixaria a liderança órfã na segunda lista — e ela voltaria sozinha ao
   * remarcar, que é a forma silenciosa de alguém virar líder sem ninguém ter
   * pedido. É o mesmo motivo pelo qual o contrato recebe o PAR e não a lista de
   * ids (ver `setUserDepartments` em `contract/src/platform.ts`).
   */
  const [chosen, setChosen] = useState<Map<string, boolean>>(
    () => new Map(user.departments.map((d) => [d.id, d.leads])),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.platform.setUserDepartments({
        params: { id: user.id },
        body: { departments: [...chosen].map(([id, leads]) => ({ id, leads })) },
      });
      return ok(r, 200, 'Não foi possível trocar os departamentos.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['plataforma', 'usuarios'] });
      /* a contagem "quantas pessoas" da tela de departamentos muda junto */
      void client.invalidateQueries({ queryKey: ['plataforma', 'departamentos'] });
      onClose();
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  /*
   * O seletor devolve a lista de ids marcados; o mapa guarda a liderança.
   * Desmarcar o setor APAGA a liderança junto — guardá-la "para quando voltar"
   * faria alguém virar líder de novo sem ter pedido.
   */
  const pick = (ids: string[]) =>
    setChosen((current) => new Map(ids.map((id) => [id, current.get(id) === true])));

  const marked = available.filter((d) => chosen.has(d.id));

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      tone="destaque"
      size="lg"
      title="Trocar os departamentos"
      descricao={user.nome}
      leading={
        <button
          type="button"
          onClick={onClose}
          aria-label="Voltar para a lista de usuários"
          className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-borda bg-superficie text-texto-suave transition-colors hover:text-texto active:scale-[0.94] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
        >
          <Icon nome="seta-esquerda" size={20} />
        </button>
      }
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        <div className="flex items-center gap-4">
          <Avatar nome={user.nome} size="xl" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-lg font-bold text-texto">{user.nome}</span>
            <span className="text-sm text-texto-suave">Trocar os departamentos</span>
          </div>
        </div>

        {available.length === 0 ? (
          <Alert tone="alerta">Esta base ainda não tem departamento cadastrado.</Alert>
        ) : (
          <div className="flex flex-col gap-4">
            <MultiSelect
              label="Departamentos"
              placeholder="Nenhum departamento"
              searchPlaceholder="Buscar departamento…"
              emptyMessage="Nenhum departamento com esse nome."
              layer="modal"
              options={available.map((d) => ({ value: d.id, label: d.nome }))}
              selected={[...chosen.keys()]}
              onChange={pick}
            />

            {/*
             * A liderança só existe DENTRO do vínculo, e por isso aparece só para
             * os setores marcados: oferecer "lidera" a um setor que a pessoa não
             * integra seria a tela propondo um estado que o banco não guarda.
             */}
            {marked.length > 0 && (
              <fieldset className="flex flex-col gap-1">
                <legend className="mb-1 text-xs font-medium text-texto">Liderança</legend>
                {marked.map((d) => (
                  <label
                    key={d.id}
                    className="flex min-h-11 items-center gap-3 text-sm text-texto"
                  >
                    <input
                      type="checkbox"
                      checked={chosen.get(d.id) === true}
                      onChange={(e) =>
                        setChosen((current) => {
                          const next = new Map(current);
                          next.set(d.id, e.target.checked);
                          return next;
                        })
                      }
                      className="size-5 rounded border-borda-forte accent-primaria-texto"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span>Lidera {d.nome}</span>
                      {/*
                       * O que a caixa concede, ao lado dela (29/10/2026): no
                       * setor com grupo do líder, marcar dá as permissões
                       * daquele grupo, somadas às do grupo da pessoa.
                       */}
                      {d.leaderGroup && !d.leaderGroup.archived && (
                        <span className="text-xs text-texto-suave">
                          Quem lidera recebe também o grupo “{d.leaderGroup.nome}”.
                        </span>
                      )}
                    </span>
                  </label>
                ))}
                {/*
                 * A marca sozinha não vale, e a tela precisa dizer.
                 *
                 * Liderar é o AND de duas coisas: esta marca (em QUAIS setores)
                 * e `tickets.tickets:liderar` no grupo (se PODE liderar). Sem a
                 * segunda, isto grava e não faz nada. Avisa em vez de recusar: dá
                 * para preparar o vínculo antes de mexer no grupo.
                 */}
                {/*
                 * Só para setor SEM grupo do líder: onde ele existe, a marca
                 * concede o grupo sozinha, e a frase seria falsa.
                 */}
                {!mayLead &&
                  marked.some(
                    (d) =>
                      chosen.get(d.id) === true &&
                      !(d.leaderGroup && !d.leaderGroup.archived),
                  ) && (
                    <p className="text-xs text-alerta">
                      O grupo desta pessoa não concede “Agir como líder”, então a marca de
                      liderança ainda não vale.
                    </p>
                  )}
              </fieldset>
            )}
          </div>
        )}

        <ModalActions className="border-t border-borda-suave pt-5">
          <Button
            type="submit"
            loading={save.isPending}
            disabled={available.length === 0}
          >
            Salvar
          </Button>
          <Button type="button" variant="contorno" onClick={onClose}>
            Cancelar
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------------- *
 * Cadastrar um colaborador — `plataforma-10`
 * ------------------------------------------------------------------------- */

/**
 * O cadastro, e o convite logo depois dele.
 *
 * ## Dois passos numa tela só, e mesmo assim dois atos
 *
 * Criar e convidar são rotas separadas, com eventos separados na trilha — e é
 * o que permite cadastrar hoje o time que começa segunda sem mandar, hoje, um
 * link que vence antes. A tela junta os dois no caminho comum porque **é o
 * caminho comum**: quem cadastra alguém normalmente quer que a pessoa entre.
 *
 * O que ela não faz é esconder que são dois: quando o cadastro dá certo e o
 * convite falha, a frase diz exatamente isso — a pessoa existe, o link não
 * saiu, e o botão de reenviar está na linha dela. Um "não foi possível criar"
 * ali mandaria alguém cadastrar de novo quem já existe.
 *
 * ## A senha não é escolhida aqui, e não há campo para ela
 *
 * Quem escolhe é a pessoa, pelo convite. Uma senha definida por terceiro é uma
 * senha que alguém mais conhece, e o primeiro acesso existe para isso.
 */
function NewUser({
  groups,
  created,
}: {
  groups: Array<{ id: string; nome: string }>;
  created: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [groupId, setGroupId] = useState('');
  const [error, setError] = useState<string | null>(null);
  /* o prazo vem da resposta do convite: escrever "72 horas" aqui é repetir
     à mão o `EXPIRY_HOURS` do serviço, e as duas cópias envelhecem sozinhas */
  const [done, setDone] = useState<{ email: string; expiresAt: string } | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const r = await api.platform.createUser({
        body: { nome: name.trim(), email: email.trim(), groupId: groupId || null },
      });
      if (r.status !== 201) {
        throw new Error(errorMessage(r.body, 'Não foi possível criar o cadastro.'));
      }
      const person = r.body;

      /*
       * O convite é a segunda chamada, e a falha dela é DIFERENTE da primeira.
       * O `name` carrega qual das duas quebrou porque a frase muda: uma manda
       * tentar de novo, a outra manda reenviar da linha.
       */
      const invite = await api.platform.inviteUser({
        params: { id: person.id },
        body: {},
      });
      if (invite.status !== 201) {
        const failure = new Error(
          errorMessage(invite.body, 'Não foi possível enviar o convite.'),
        );
        failure.name = 'convite';
        throw failure;
      }
      return { person, expiresAt: invite.body.expiresAt };
    },
    onSuccess: ({ person, expiresAt }) => {
      created();
      setName('');
      setEmail('');
      setGroupId('');
      setError(null);
      setDone({ email: person.email, expiresAt });
      setOpen(false);
    },
    onError: (e: Error) => {
      created();
      setDone(null);
      setError(
        e.name === 'convite'
          ? `${e.message} O cadastro FOI criado — use “Reenviar convite” na linha dele na lista.`
          : e.message,
      );
    },
  });

  return (
    <Card
      title="Cadastrar um colaborador"
      descricao="Ele recebe por e-mail o link para criar a própria senha."
    >
      <div className="flex flex-col gap-3">
        {done && (
          <Alert tone="sucesso" title="Cadastro criado e convite enviado.">
            <strong>{done.email}</strong> recebeu o link para criar a senha. Ele vale até{' '}
            {formatInstant(done.expiresAt)} — passando disso, use “Reenviar convite” na
            linha dele na lista.
          </Alert>
        )}

        {error && <Alert tone="erro">{error}</Alert>}

        {!open ? (
          <div>
            <Button variant="contorno" onClick={() => setOpen(true)}>
              Novo colaborador
            </Button>
          </div>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              setDone(null);
              create.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Nome"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Maria Silva"
              />
              <Field
                label="E-mail"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="maria.silva@evogard.com.br"
                help="É por ele que o convite chega, e é com ele que a pessoa entra."
              />
            </div>

            <div className="max-w-xs">
              <Select
                label="Grupo"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                /*
                 * "Sem grupo" é um estado legítimo, e a ajuda diz o que ele faz:
                 * a negação por omissão fica visível antes do clique, e não
                 * depois — quando a pessoa liga dizendo que não vê nada.
                 */
                help="Sem grupo, a pessoa entra e não pode nada."
              >
                <option value="">Sem grupo</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.nome}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="submit"
                loading={create.isPending}
                disabled={name.trim().length < 2 || !email.includes('@')}
              >
                Cadastrar e convidar
              </Button>
              <Button variant="contorno" type="button" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}

/** "1 pessoa", "2 pessoas" — e nunca "1 pessoa(s)". */
function labelTotal(total: number): string {
  return total === 1 ? '1 pessoa' : `${total} pessoas`;
}
